import {
  InputVariable,
  OutputVariable,
  EmissionsVariable,
  LocalVariable,
  ResultVariable,
  ProcessVariableListKey,
} from './emissionsModelSchema';
import assertNever from '@watershed/shared-util/src/assertNever';

/**
 * VariableLocation classes represent the location in an emissions model of a
 * process variable (e.g. an InputVariable). VariableFieldLocations represent
 * the location of a specific field of a process variable (e.g. the description
 * field of an InputVariable).
 *
 * Anything that has access to an emissions model and one of these locations can
 * go get the thing (variable or field value) that it needs. That means we don't
 * always have to pass around the actual thing from function to function, e.g.
 * down a React component tree. In the emissions model editor UI, specifically,
 * all components can access the model through the centralized store; so by
 * passing just locations we get to simplify interfaces, make components more
 * self-contained, and give them more power to control when they re-render.
 *
 * We cache locations that we've seen before so the value you get from the
 * constructor or `create` has referential equality everywhere we're pointing to
 * the same place. That referential equality is useful for controlling React
 * re-renders, which we do in the emissions model editor.
 *
 * We're calling these "locations" to differentiate this more involved concept
 * from the simpler array-of-strings "paths" that we have in various places
 * (e.g. emissionsModelPaths, source maps' importPaths and modelPaths).
 *
 * The code below is set up to enables strict typing: every variable location
 * has a fixed list key; every field location has a fixed list key and a fixed
 * field name; and the typechecker is able to follow it all, providing accurate
 * errors and hints. (We haven't yet figured out a less repetitious, more
 * abstract approach that provides the same type safety.)
 */

type LocationCache<T> = Map<string, T>;
function getCached<R extends { toString(): string }>(
  cache: LocationCache<R>,
  v: R
): R {
  const cacheKey = v.toString();
  const cached = cache.get(cacheKey);
  if (!!cached) {
    return cached as R;
  }
  cache.set(cacheKey, v);
  return v as R;
}

type FieldsForListKey = {
  inputs: keyof InputVariable;
  outputs: keyof OutputVariable;
  emissions: keyof EmissionsVariable;
  locals: keyof LocalVariable;
  results: keyof ResultVariable;
};

// We're using weak types for the variable cache to avoid a bunch more typing
// hassle (there's already enough in this module!). We know that the types will
// work out right; we just aren't bothering to create a strongly typed cache and
// getter for every single location class below. (We have some unit tests to
// ensure we're not lying about it "working out right".)
const variableLocationCache = new Map<string, any>();

abstract class LocationBase<T extends ReadonlyArray<string>> {
  protected readonly args: T;
  constructor(cache: LocationCache<LocationBase<T>>, ...args: T) {
    this.args = args;
    return getCached(cache, this);
  }
  toArray(): T {
    return this.args;
  }
  toString(): string {
    return this.args.join('.');
  }
}

// Deliberately not exported: code outside this module should always be using a
// specific subclass.
abstract class VariableFieldLocationBase<
  K extends ProcessVariableListKey,
  F extends FieldsForListKey[K],
  A extends [string, K, string, F] = [string, K, string, F],
> extends LocationBase<A> {
  readonly nodeName = this.args[0];
  readonly listKey = this.args[1];
  readonly variableName = this.args[2];
  readonly fieldName = this.args[3];
  constructor(...args: A) {
    super(variableLocationCache, ...args);
  }
}

// Deliberately not exported: code outside this module should always be using a
// specific subclass.
class VariableLocationBase<
  K extends ProcessVariableListKey,
  A extends [string, K, string] = [string, K, string],
> extends LocationBase<A> {
  readonly nodeName = this.args[0];
  readonly listKey = this.args[1];
  readonly variableName = this.args[2];
  constructor(...args: A) {
    super(variableLocationCache, ...args);
  }
}

// Without the generic on these variable location subclasses, we lose the specific
// value of the fieldName that was provided. Without the generic all sites would
// know, e.g., that fieldName is a keyof InputVariable, but they wouldn't know
// which key. With the generic, the instance has its specific key value
// associated with it (e.g. InputVariableField<"displayName">).
export class InputVariableFieldLocation<
  C extends keyof InputVariable = keyof InputVariable,
> extends VariableFieldLocationBase<'inputs', C> {
  static create<C extends keyof InputVariable>(
    n: string,
    v: string,
    f: C
  ): InputVariableFieldLocation<C> {
    return new InputVariableFieldLocation(n, 'inputs', v, f);
  }
  /**
   * Remove the field to get a (more generic) variable location.
   */
  removeField(): InputVariableLocation {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return InputVariableLocation.create(this.nodeName, this.variableName);
  }
}

export class InputVariableLocation extends VariableLocationBase<'inputs'> {
  static create(n: string, v: string): InputVariableLocation {
    return new InputVariableLocation(n, 'inputs', v);
  }
  /**
   * Add a field to get a (more specific) field location.
   */
  addField<F extends keyof InputVariable>(
    fieldName: F
  ): InputVariableFieldLocation<F> {
    return InputVariableFieldLocation.create(
      this.nodeName,
      this.variableName,
      fieldName
    );
  }
}

export class OutputVariableFieldLocation<
  F extends keyof OutputVariable = keyof OutputVariable,
> extends VariableFieldLocationBase<'outputs', F> {
  static create<C extends keyof OutputVariable>(
    n: string,
    v: string,
    f: C
  ): OutputVariableFieldLocation<C> {
    return new OutputVariableFieldLocation(n, 'outputs', v, f);
  }
  /**
   * Remove the field to get a (more generic) variable location.
   */
  removeField(): OutputVariableLocation {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return OutputVariableLocation.create(this.nodeName, this.variableName);
  }
}

export class OutputVariableLocation extends VariableLocationBase<'outputs'> {
  static create(n: string, v: string): OutputVariableLocation {
    return new OutputVariableLocation(n, 'outputs', v);
  }
  /**
   * Add a field to get a (more specific) field location.
   */
  addField<F extends keyof OutputVariable>(
    fieldName: F
  ): OutputVariableFieldLocation<F> {
    return OutputVariableFieldLocation.create(
      this.nodeName,
      this.variableName,
      fieldName
    );
  }
}

export class EmissionsVariableFieldLocation<
  F extends keyof EmissionsVariable = keyof EmissionsVariable,
> extends VariableFieldLocationBase<'emissions', F> {
  static create<C extends keyof EmissionsVariable>(
    n: string,
    v: string,
    f: C
  ): EmissionsVariableFieldLocation<C> {
    return new EmissionsVariableFieldLocation(n, 'emissions', v, f);
  }
  /**
   * Remove the field to get a (more generic) variable location.
   */
  removeField(): EmissionsVariableLocation {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return EmissionsVariableLocation.create(this.nodeName, this.variableName);
  }
}

export class EmissionsVariableLocation extends VariableLocationBase<'emissions'> {
  static create(n: string, v: string): EmissionsVariableLocation {
    return new EmissionsVariableLocation(n, 'emissions', v);
  }
  /**
   * Add a field to get a (more specific) field location.
   */
  addField<F extends keyof EmissionsVariable>(
    fieldName: F
  ): EmissionsVariableFieldLocation<F> {
    return EmissionsVariableFieldLocation.create(
      this.nodeName,
      this.variableName,
      fieldName
    );
  }
}

export class LocalVariableFieldLocation<
  F extends keyof LocalVariable = keyof LocalVariable,
> extends VariableFieldLocationBase<'locals', F> {
  static create<C extends keyof LocalVariable>(
    n: string,
    v: string,
    f: C
  ): LocalVariableFieldLocation<C> {
    return new LocalVariableFieldLocation(n, 'locals', v, f);
  }
  /**
   * Remove the field to get a (more generic) variable location.
   */
  removeField(): LocalVariableLocation {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return LocalVariableLocation.create(this.nodeName, this.variableName);
  }
}

export class LocalVariableLocation extends VariableLocationBase<'locals'> {
  static create(n: string, v: string): LocalVariableLocation {
    return new LocalVariableLocation(n, 'locals', v);
  }
  /**
   * Add a field to get a (more specific) field location.
   */
  addField<F extends keyof LocalVariable>(
    fieldName: F
  ): LocalVariableFieldLocation<F> {
    return LocalVariableFieldLocation.create(
      this.nodeName,
      this.variableName,
      fieldName
    );
  }
}

export class ResultVariableFieldLocation<
  F extends keyof ResultVariable = keyof ResultVariable,
> extends VariableFieldLocationBase<'results', F> {
  static create<C extends keyof ResultVariable>(
    n: string,
    v: string,
    f: C
  ): ResultVariableFieldLocation<C> {
    return new ResultVariableFieldLocation(n, 'results', v, f);
  }
  /**
   * Remove the field to get a (more generic) variable location.
   */
  removeField(): ResultVariableLocation {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return ResultVariableLocation.create(this.nodeName, this.variableName);
  }
}

export class ResultVariableLocation extends VariableLocationBase<'results'> {
  static create(n: string, v: string): ResultVariableLocation {
    return new ResultVariableLocation(n, 'results', v);
  }
  /**
   * Add a field to get a (more specific) field location.
   */
  addField<F extends keyof ResultVariable>(
    fieldName: F
  ): ResultVariableFieldLocation<F> {
    return ResultVariableFieldLocation.create(
      this.nodeName,
      this.variableName,
      fieldName
    );
  }
}

export type VariableFieldLocation =
  | InputVariableFieldLocation<keyof InputVariable>
  | OutputVariableFieldLocation<keyof OutputVariable>
  | EmissionsVariableFieldLocation<keyof EmissionsVariable>
  | LocalVariableFieldLocation<keyof LocalVariable>
  | ResultVariableFieldLocation<keyof ResultVariable>;

export type VariableLocation =
  | InputVariableLocation
  | OutputVariableLocation
  | EmissionsVariableLocation
  | LocalVariableLocation
  | ResultVariableLocation;

export function isInputOutputVariableLocation(
  vp: VariableLocation
): vp is InputVariableLocation | OutputVariableLocation {
  return vp.listKey === 'inputs' || vp.listKey === 'outputs';
}

export function getVariableLocation(
  nodeName: string,
  listKey: ProcessVariableListKey,
  variableName: string
): VariableLocation {
  switch (listKey) {
    case 'emissions':
      return EmissionsVariableLocation.create(nodeName, variableName);
    case 'inputs':
      return InputVariableLocation.create(nodeName, variableName);
    case 'outputs':
      return OutputVariableLocation.create(nodeName, variableName);
    case 'locals':
      return LocalVariableLocation.create(nodeName, variableName);
    case 'results':
      return ResultVariableLocation.create(nodeName, variableName);
    default:
      assertNever(listKey);
  }
}

const importParameterLocationCache = new Map<string, ImportParameterLocation>();

/**
 * Represents the location through an emissions model to an import parameter.
 */
export class ImportParameterLocation extends LocationBase<[string, string]> {
  readonly nodeName = this.args[0];
  readonly parameterName = this.args[1];
  constructor(...args: [string, string]) {
    super(importParameterLocationCache, ...args);
  }

  static create(
    nodeName: string,
    parameterName: string
  ): ImportParameterLocation {
    return new ImportParameterLocation(nodeName, parameterName);
  }
}
